import { DownOutlined, SearchOutlined } from '@ant-design/icons';
import { Dropdown, Input, Menu } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import MenuItem from 'antd/lib/menu/MenuItem';
import AmountBalance from 'components/UI/AmountBalance';
import AmountToggle from 'components/UI/AmountToggle';
import DeliveryDateSelector from 'components/UI/DeliveryDateSelector';
import { useGetBalanceQuery } from 'services/ecommerceApi';
import { sessionSelectors } from 'store/state/selectors';

import { setCustomerIdSelected } from 'services/config';
import { buyIntentionActions, sessionActions } from 'store/state/slices';
import * as S from './styles';

export default function UserDetailsBar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [showBalance, setShowBalance] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const { customers, producerUrl } = useSelector(sessionSelectors.getRoot);

  const selectedCustomerId = useSelector(
    sessionSelectors.getSelectedCustomerId,
  );

  const isAuthenticated = useSelector(sessionSelectors.getAuthenticated);
  const isProducerAuthenticated = useSelector(
    sessionSelectors.isProducerAuthenticated,
  );

  const producerUrls = useSelector(sessionSelectors.getUrlsProducer);

  const [urlSelected, setUrlSelected] = useState<string>('');

  useEffect(() => {
    setUrlSelected(producerUrl);
  }, [producerUrl]);

  const customerSelected = customers.find(
    (customer) => customer.id === selectedCustomerId,
  );

  const {
    data: balance,
    isLoading: isLoadingBalance,
    isFetching: isFetchingBalance,
    isError: err,
  } = useGetBalanceQuery(customerSelected?.accountCode || '', {
    skip: !customerSelected?.accountCode,
  });

  const handleChangeUrlSelected = (value: string) => {
    setUrlSelected(value);
    navigate(`${location.pathname.replace(producerUrl, value)}`);
  };

  const toggleBalance = useCallback(() => {
    setShowBalance((value) => !value);
  }, []);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((value) => !value);
  }, []);

  const setSelectedCustomer = useCallback(
    (value) => {
      dispatch(sessionActions.setSelectedCustomer(value));
      dispatch(sessionActions.setSelectedCustomerFilter(value));

      const customerData = customers.find((customer) => customer.id === value);
      dispatch(buyIntentionActions.setCustomer(customerData));
      setCustomerIdSelected(value);
    },
    [dispatch, customers],
  );

  const formatCustomerName = (customerName: string) => {
    if (customerName.length <= 22) return customerName;

    return `${customerName.substring(0, 22)}...`;
  };

  if (!isAuthenticated) {
    return null;
  }

  const producerMenu = (
    <Menu>
      {producerUrls.map((url) => (
        <MenuItem
          key={url.url}
          onClick={() => handleChangeUrlSelected(url.url)}
        >
          {url.url}
        </MenuItem>
      ))}
    </Menu>
  );

  const filteredCustomers = customers.filter(({ accountCode, name }) => {
    const searchLower = customerSearchTerm.toLowerCase();
    return (
      accountCode.toLowerCase().includes(searchLower) ||
      name.toLowerCase().includes(searchLower)
    );
  });

  const clientMenu = (
    <Menu>
      <div style={{ padding: '8px' }}>
        <S.SearchInputWrapper>
          <Input
            placeholder="Buscar cliente..."
            value={customerSearchTerm}
            onChange={(e) => setCustomerSearchTerm(e.target.value)}
            prefix={<SearchOutlined />}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </S.SearchInputWrapper>
      </div>
      {filteredCustomers.length > 0 ? (
        filteredCustomers.map(({ id, accountCode, name }) => (
          <MenuItem
            key={`${id}-key-customer`}
            onClick={() => {
              setSelectedCustomer(id);
              setCustomerSearchTerm('');
            }}
          >
            {`${accountCode} - ${formatCustomerName(name)}`}
          </MenuItem>
        ))
      ) : (
        <MenuItem disabled>
          Nenhum cliente encontrado
        </MenuItem>
      )}
    </Menu>
  );

  const selectedCustomer = customers.find(
    ({ id }) => id === selectedCustomerId,
  );

  const headerSelector =
    isProducerAuthenticated && !!(producerUrls?.length >= 1) ? (
      <S.Section>
        <S.Label>Produtor:</S.Label>
        {producerUrls.length > 1 ? (
          <Dropdown overlay={producerMenu} trigger={['click']}>
            <S.CustomerButton>
              <S.CustomerText>{urlSelected}</S.CustomerText>
              <DownOutlined />
            </S.CustomerButton>
          </Dropdown>
        ) : (
          <S.CustomerButton>
            <S.CustomerText>{urlSelected}</S.CustomerText>
          </S.CustomerButton>
        )}
      </S.Section>
    ) : (
      !!(customers?.length >= 1) && (
        <S.Section>
          <S.Label>Cliente:</S.Label>
          {customers.length > 1 ? (
            <Dropdown overlay={clientMenu} trigger={['click']}>
              <S.CustomerButton>
                <S.CustomerText>
                  {selectedCustomer?.accountCode} -{' '}
                  {formatCustomerName(selectedCustomer?.name || '')}
                </S.CustomerText>
                <DownOutlined />
              </S.CustomerButton>
            </Dropdown>
          ) : (
            <S.CustomerButton>
              <S.CustomerText>
                {selectedCustomer?.accountCode} -{' '}
                {formatCustomerName(selectedCustomer?.name || '')}
              </S.CustomerText>
            </S.CustomerButton>
          )}
        </S.Section>
      )
    );

  return (
    <S.Container>
      <S.Content>
        {/* Desktop/Tablet Layout */}
        <S.DesktopLayout>
          {headerSelector}

          <S.VerticalDivider />

          <S.Section>
            <S.Label>Entrega (ENP):</S.Label>
            <DeliveryDateSelector showLabel={false} />
          </S.Section>

          <S.VerticalDivider />

          <S.Section>
            <S.Label>Valor Disp.:</S.Label>
            <S.AmountContainer>
              <AmountBalance
                showBalance={showBalance}
                isLoadingBalance={isLoadingBalance}
                isFetchingBalance={isFetchingBalance}
                balance={balance}
              />
              <AmountToggle
                showValue={!showBalance}
                warning={err}
                setShowValue={toggleBalance}
              />
            </S.AmountContainer>
          </S.Section>

          <S.ExpandButton onClick={toggleExpanded}>
            Esconder Entrega & Valor
            <DownOutlined
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            />
          </S.ExpandButton>
        </S.DesktopLayout>

        {/* Mobile Layout */}
        <S.MobileLayout>
          <S.MobileHeader>
            {headerSelector}

            <S.MobileToggleButton onClick={toggleExpanded}>
              {isExpanded ? 'Esconder Entrega & Valor' : 'Exibir Valor'}
              <DownOutlined
                style={{
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </S.MobileToggleButton>
          </S.MobileHeader>

          <S.MobileExpandedContent isExpanded={isExpanded}>
            <S.HorizontalDivider />
            <S.MobileRow>
              <S.Section>
                <S.Label>Entrega (ENP):</S.Label>
                <DeliveryDateSelector showLabel={false} />
              </S.Section>

              <S.VerticalDivider showOnMobile />

              <S.Section>
                <S.Label>Valor Disp.:</S.Label>
                <S.AmountContainer>
                  <AmountBalance
                    showBalance={showBalance}
                    isLoadingBalance={isLoadingBalance}
                    isFetchingBalance={isFetchingBalance}
                    balance={balance}
                  />
                  <AmountToggle
                    showValue={!showBalance}
                    warning={err}
                    setShowValue={toggleBalance}
                  />
                </S.AmountContainer>
              </S.Section>
            </S.MobileRow>
          </S.MobileExpandedContent>
        </S.MobileLayout>
      </S.Content>
    </S.Container>
  );
}
